'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Search, X } from 'lucide-react';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { HeroBanner, type HeroBannerPageTitle } from '@/components/frontend/common/hero-banner';
import { BlogPostsGrid } from '@/components/frontend/blog/blog-posts-grid';
import { BlogPostSkeleton } from '@/components/frontend/blog/blog-post-skeleton';
import { Pagination } from '@/components/ui/adminUi/pagination';
import type { BlogPost } from '@/lib/api/blog';

interface BlogPageContentProps {
  locale?: string;
  /** SSR: first page of posts rendered into the initial HTML */
  initialPosts?: BlogPost[];
  initialTotal?: number;
  initialTotalPages?: number;
  initialPageTitle?: HeroBannerPageTitle | null;
}

const ITEMS_PER_PAGE = 18;

export function BlogPageContent({ locale: propLocale, initialPosts, initialTotal, initialTotalPages, initialPageTitle }: BlogPageContentProps) {
  const t = useTranslations('blog');
  const locale = useLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts ?? []);
  const [isLoading, setIsLoading] = useState(!initialPosts?.length);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(initialTotal ?? 0);
  const [totalPages, setTotalPages] = useState(initialTotalPages ?? 0);
  // The server already rendered page 1; only refetch once the user paginates or searches.
  const canUseServerPosts = useRef(!!initialPosts?.length);

  const activeLocale = propLocale || locale;

  // Reset to page 1 when search query or locale changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeLocale, searchQuery]);

  useEffect(() => {
    if (canUseServerPosts.current && currentPage === 1 && !searchQuery.trim()) {
      canUseServerPosts.current = false;
      setIsLoading(false);
      return;
    }
    const fetchPosts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Include search query in API call to search across all posts
        const searchParam = searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : '';
        
        const response = await fetch(`/api/blog?locale=${activeLocale}&page=${currentPage}&limit=${ITEMS_PER_PAGE}${searchParam}`);
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        const data = await response.json();

        // Handle both response formats: { posts: [...] } or direct array
        const postsArray = data.posts || (Array.isArray(data) ? data : []);

        // Update totals from response (total count includes all matching posts, not just current page)
        if (data.total !== undefined) {
          setTotalPosts(data.total);
          setTotalPages(data.totalPages || Math.ceil(data.total / ITEMS_PER_PAGE));
        }

        // Transform the content from JSON array to HTML string
        const transformedPosts: BlogPost[] = postsArray.map((post: any) => ({
          ...post,
          content: typeof post.content === 'string' ? (() => {
            try {
              const parsed = JSON.parse(post.content);
              if (Array.isArray(parsed)) {
                return parsed.join('');
              }
              return parsed;
            } catch {
              return post.content;
            }
          })() : post.content,
        }));

        setPosts(transformedPosts);
      } catch (err) {

        setError(err instanceof Error ? err.message : 'Failed to fetch blog posts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [activeLocale, currentPage, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <HeroBanner
        page="blog"
        title={t('hero.title') || 'Blogs'}
        description={t('hero.description') || 'Stay updated with the latest MLM software news, tips, and insights.'}
        pill={t('hero.pill') || 'Blog'}
        className="border-b border-gray-200 dark:border-gray-900"
        initialPageTitle={initialPageTitle}
      />
      <SectionWrapper variant="default" padding="md" className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative z-10 container">
          {/* Header with Title and Search */}
          <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Title with Post Count */}
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {t('hero.title') || 'Blogs'}
              <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                ({totalPosts === 1
                  ? t('results.singular', { count: totalPosts }) || `${totalPosts} post found`
                  : t('results.plural', { count: totalPosts }) || `${totalPosts} posts found`})
              </span>
            </h2>

            {/* Search Bar */}
            <form onSubmit={(e) => e.preventDefault()} className="w-full sm:w-auto lg:w-[400px]">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search.placeholder') || 'Search blog posts...'}
                  className={`w-full py-3.5 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 transition-colors text-base ${
                    searchQuery ? 'pl-12 pr-12' : 'pl-12 pr-4'
                  }`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {isLoading ? (
            <BlogPostSkeleton count={6} />
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery ? t('noResults') || 'No posts found matching your search.' : t('noPosts') || 'No blog posts available.'}
              </p>
            </div>
          ) : (
            <>
              <BlogPostsGrid posts={posts} />
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={ITEMS_PER_PAGE}
                    totalItems={totalPosts}
                    onPageChange={setCurrentPage}
                    className="bg-white dark:bg-gray-900 rounded-xl shadow-sm"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </SectionWrapper>
    </div>
  );
}

